"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "motion/react";
import { Footer } from "@/components/layout/footer";
import { MainNavigation } from "@/components/layout/navbar";
// Example Unsplash images for restaurants/food
const IMAGE_URLS = [
  "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=800&q=80", // food
  "https://images.unsplash.com/photo-1516685018646-5499d0a7c104?auto=format&fit=crop&w=800&q=80", // bar
  "https://images.unsplash.com/photo-1467003909585-2f8a72700288?auto=format&fit=crop&w=800&q=80", // table
];

// Brand Theme
const THEME = {
  colors: {
    primary: "#1D3557",
    accent: "#FFB703",
    bgGradient: "from-yellow-50 via-white to-orange-50",
    button: "#E63946",
    buttonHover: "#FFB703",
  },
  fonts: {
    display: "Playfair Display, serif",
    body: "Inter, Arial, sans-serif",
  },
};

// Motion Variants
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};
const stagger = {
  visible: {
    transition: { staggerChildren: 0.14 },
  },
};

const sections = [
  {
    icon: "🍽️",
    title: "Discover & Crave",
    description:
      "Browse Malawi’s best menus, see mouth-watering photos, and order your favorite dishes for pickup, dine-in, or delivery. Find top-rated venues by cuisine, mood, or proximity.",
    image: IMAGE_URLS[0],
  },
  {
    icon: "🪑",
    title: "Book & Dine",
    description:
      "Reserve a table, pre-order for your arrival, and skip the wait. Enjoy seamless on-table QR ordering and track your food’s journey in real time.",
    image: IMAGE_URLS[2],
  },
  {
    icon: "🥂",
    title: "Connect & Enjoy",
    description:
      "Rate, tip, and review your experience. Earn loyalty rewards, invite friends, and unlock exclusive offers. Inde Menu is designed for food lovers and hospitality fans.",
    image: IMAGE_URLS[1],
  },
];

function AboutIndeMenu() {
  return (
    <main
      className={`font-sans min-h-screen bg-gradient-to-b ${THEME.colors.bgGradient} flex flex-col justify-between`}
      style={{ fontFamily: THEME.fonts.body }}
    >
      <MainNavigation />

      {/* Hero Section */}
      <section className="w-full flex flex-col items-center justify-center py-12 px-4">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="max-w-2xl mx-auto text-center"
        >
          <h1
            className="text-4xl md:text-5xl font-extrabold mb-4"
            style={{
              fontFamily: THEME.fonts.display,
              color: THEME.colors.primary,
            }}
          >
            Welcome to Inde Menu
          </h1>
          <p className="text-lg md:text-xl text-gray-700 mb-6">
            Revolutionizing Malawi’s restaurant & bar scene. <br />
            Your cravings, bookings, and orders—all in one seamless mobile
            experience.
          </p>
        </motion.div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="mt-4"
        >
          <Link href="/vendors" 
              className="inline-block bg-[color:#E63946] hover:bg-[color:#FFB703] text-white font-bold py-3 px-6 rounded-full shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-orange-300"
              aria-label="Explore Menu"
            >
              Explore Menus
             
          </Link>
        </motion.div>
      </section>

      {/* Core Features */}
      <section className="w-full py-8 px-2 md:px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="flex flex-col justify-center items-center gap-10 max-w-5xl mx-auto"
        >
          {sections.map((section, idx) => (
            <motion.div
              key={section.title}
              variants={fadeUp}
              className="w-full flex flex-col md:flex-row items-center gap-6 bg-white rounded-2xl shadow-lg p-6 md:p-10"
              style={{
                background:
                  idx % 2 === 0
                    ? "linear-gradient(90deg, #fffbe6 0%, #fff 100%)"
                    : "linear-gradient(90deg, #ffe0e0 0%, #fff 100%)",
              }}
            >
              <Image
                src={section.image}
                alt={section.title}
                width={320}
                height={220}
                className="rounded-lg object-cover shadow-md"
                style={{ minWidth: 180 }}
                priority
              />
              <div className="flex-1 flex flex-col items-center md:items-start text-center md:text-left">
                <div className="text-4xl mb-2">{section.icon}</div>
                <h2
                  className="text-2xl md:text-3xl font-bold mb-3"
                  style={{
                    color: THEME.colors.primary,
                    fontFamily: THEME.fonts.display,
                  }}
                >
                  {section.title}
                </h2>
                <p className="text-gray-800 text-lg md:text-xl">
                  {section.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Call to Action */}
      <section className="w-full py-10 px-3 md:px-0 flex flex-col items-center justify-center">
        <motion.div initial="hidden" whileInView="visible" variants={fadeUp}>
          <div className="max-w-2xl text-center mx-auto mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-orange-700 mb-3">
              Hungry for more?
            </h2>
            <p className="text-lg text-gray-700 mb-5">
              Inde Menu makes dining out and ordering in easier, faster, and
              more rewarding. Ready to indulge?
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link href="/signup" 
                className="bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-6 rounded-full shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-orange-300"
                aria-label="Sign Up"
              >
                Sign Up Now
               
            </Link>
            <Link href="/pages/contact" 
                className="bg-white text-orange-700 font-semibold py-3 px-6 rounded-full border border-orange-700 shadow-md hover:bg-orange-50 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-orange-200"
                aria-label="Contact Us"
              >
                Contact Us
              
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <Footer />
    </main>
  );
}

export default AboutIndeMenu;
